import React from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, FormGroup,
    MenuItem, Paper, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DICTIONARY_INFO, FIELD_INFO, VOICE_INFO, sortVoices, voiceLabel } from '../../Mobx/AudioCatalog';
import {
    GENDERS, GENDER_LABELS, LISTS, MODE_FILL_MISSING, MODE_LABELS, MODE_REPLACE_WITH_BACKUP, MODE_TRIAL, PROVIDER_LABELS, SOURCE_FILTER, SOURCE_IDS,
    backupFolderPattern, fallbackProviderOf, fieldLanguages, formatNumber, groupLimitsOf, impliedLanguages, isReplaceMode, isTrialMode, isValidVoice, languageLabel,
    limitsOf, pacingOf, parseIds, supportsGroup, supportsMode, trialFolderPattern, voiceOf, withDictionary, withFields, withGroup, withMode, withVoice, PATH_STYLE
} from './bulkAudioForm';

/** One voice: a menu of the Gemini voices (Google), or a text field for the id of the voice (Inworld) */
const VoiceField = ({ form, setForm, options, provider, language, gender }) => {
    const value = voiceOf(form, options, provider, language, gender);
    const choose = (voice) => setForm(withVoice(form, provider, language, gender, voice));
    const testId = `voice-${provider}-${language}-${gender}`;

    if (provider === 'google') {
        const voices = sortVoices(options?.voices?.google ?? Object.keys(VOICE_INFO));
        return (
            <TextField
                select
                size="small"
                label={GENDER_LABELS[gender]}
                value={voices.includes(value) ? value : ''}
                onChange={(event) => choose(event.target.value)}
                sx={{ minWidth: 210 }}
                SelectProps={{ SelectDisplayProps: { 'data-testid': testId } }}
            >
                {voices.map((name) => <MenuItem key={name} value={name}>{voiceLabel(name)}</MenuItem>)}
            </TextField>
        );
    }
    return (
        <TextField
            size="small"
            label={GENDER_LABELS[gender]}
            value={value}
            onChange={(event) => choose(event.target.value)}
            error={!isValidVoice(options, provider, value)}
            helperText={isValidVoice(options, provider, value) ? ' ' : 'מזהה קול לא תקין'}
            sx={{ minWidth: 210 }}
            inputProps={{ dir: 'ltr', 'data-testid': testId }}
        />
    );
};

/** The voices of one provider: a row for every language of the run, a field for every gender */
const VoicesGrid = ({ form, setForm, options, provider, languages, genders, title, note }) => (
    <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
        {note && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{note}</Typography>}
        {languages.map((language) => (
            <Box key={language} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography sx={{ minWidth: 130, pt: 1 }}>{languageLabel(language)}</Typography>
                {genders.map((gender) => (
                    <VoiceField key={gender} form={form} setForm={setForm} options={options} provider={provider} language={language} gender={gender} />
                ))}
            </Box>
        ))}
    </Box>
);

const SectionTitle = ({ children }) => <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>{children}</Typography>;

/**
 * The form of a new bulk run: which entries, what is spoken, who speaks, the limits.
 * It only edits the form (form / setForm); checking the plan and starting are in BulkRunPlan.
 */
const BulkRunForm = ({ form, setForm, options, errors, showErrors, planning, onPlan }) => {
    const update = (patch) => setForm({ ...form, ...patch });
    const limits = limitsOf(options);
    const dictionaries = options?.dictionaries?.length ? options.dictionaries : [{ key: 'heb_to_eng' }, { key: 'eng_to_heb' }];
    const fieldMap = fieldLanguages(options, form.dictionary);
    const fieldNames = Object.keys(fieldMap);
    const languages = impliedLanguages(options, form.dictionary, form.fields).filter((language) => form.languages.includes(language));
    const genders = GENDERS.filter((gender) => form.genders.includes(gender));
    const fallbackProvider = fallbackProviderOf(form);
    const parsedIds = parseIds(form.idsText);
    const replacing = isReplaceMode(form.mode);
    const trial = isTrialMode(form.mode);
    // a trial is always a group run
    const grouping = Boolean(form.useGroup) || trial;
    const groupLimits = groupLimitsOf(options);
    const replaceSupported = supportsMode(options, MODE_REPLACE_WITH_BACKUP);
    const trialSupported = supportsMode(options, MODE_TRIAL);
    const groupSupported = supportsGroup(options);

    const toggle = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

    const idsHelper = form.idsText.trim() === ''
        ? `מזהים של 24 תווים, מופרדים ברווח, בפסיק או בשורה חדשה (עד ${formatNumber(limits.maxEntries)}).`
        : [
            `זוהו ${formatNumber(parsedIds.ids.length)} מזהים תקינים`,
            parsedIds.duplicates > 0 ? `${formatNumber(parsedIds.duplicates)} כפולים הוסרו` : '',
            parsedIds.invalid.length > 0 ? `${formatNumber(parsedIds.invalid.length)} אינם תקינים (למשל ${parsedIds.invalid[0].slice(0, 24)})` : '',
        ].filter(Boolean).join(', ') + '.';

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">הרצה חדשה</Typography>

            <SectionTitle>מצב ההרצה</SectionTitle>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={form.mode}
                onChange={(event, value) => value && setForm(withMode(form, value))}
                aria-label="מצב ההרצה"
            >
                <ToggleButton value={MODE_FILL_MISSING} data-testid="bulk-mode-fill-missing">{MODE_LABELS[MODE_FILL_MISSING]}</ToggleButton>
                <ToggleButton value={MODE_REPLACE_WITH_BACKUP} disabled={!replaceSupported} data-testid="bulk-mode-replace">{MODE_LABELS[MODE_REPLACE_WITH_BACKUP]}</ToggleButton>
                <ToggleButton value={MODE_TRIAL} disabled={!trialSupported} data-testid="bulk-mode-trial">{MODE_LABELS[MODE_TRIAL]}</ToggleButton>
            </ToggleButtonGroup>
            {!replaceSupported && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="bulk-mode-replace-unavailable">
                    ההחלפה עם גיבוי תהיה זמינה אחרי שהשרת יתעדכן. עד אז אפשר ליצור רק קבצים שחסרים.
                </Typography>
            )}
            {!trialSupported && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="bulk-mode-trial-unavailable">
                    מצב הניסוי יהיה זמין אחרי שהשרת יתעדכן.
                </Typography>
            )}
            {trial ? (
                <Alert severity="info" sx={{ mt: 1, mb: 1 }} data-testid="bulk-mode-trial-note">
                    ניסוי מכין את הקבצים בדיוק כמו הרצה אמיתית, אבל כותב אותם לתיקייה משלהם (<bdi dir="ltr" style={PATH_STYLE}>{trialFolderPattern(options)}</bdi>)
                    ולא לקבצי האודיו של האתר, ולכן אף אחד לא שומע אותם באתר. הניסוי תמיד רץ בקבוצות. מאזינים לקבצים ולהקלטות המלאות במסך ההרצה,
                    ואם הכול תקין מריצים אותם באמת. שום קובץ שקיים לא נוגע ולא נמחק.
                </Alert>
            ) : replacing ? (
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }} data-testid="bulk-mode-replace-note">
                    ההרצה מחליפה קבצי אודיו שכבר קיימים (למשל קול שלא נשמע טוב) בקבצים חדשים. לפני כל החלפה הקובץ הישן מועתק
                    לתיקיית הגיבוי של ההרצה (<bdi dir="ltr" style={PATH_STYLE}>{backupFolderPattern(options)}</bdi>) והעותק נבדק. אם הגיבוי של קובץ נכשל, הקובץ
                    לא מוחלף וההרצה מושהית. שום קובץ לא נמחק, וקבצים שלא קיימים לא נוצרים (לזה יש את המצב "{MODE_LABELS[MODE_FILL_MISSING]}").
                    קודם בודקים תוכנית (בלי לשנות כלום), ואחר כך מאשרים את המספרים ומתחילים.
                </Alert>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }} data-testid="bulk-mode-fill-note">
                    ההרצה יוצרת רק קבצי אודיו שחסרים. קובץ שכבר קיים לא נדרס ולא נמחק. קודם בודקים תוכנית (בלי ליצור כלום),
                    ואחר כך מאשרים את המספרים ומתחילים.
                </Typography>
            )}

            <SectionTitle>הקראה בקבוצות</SectionTitle>
            <FormControlLabel
                control={(
                    <Switch
                        checked={grouping}
                        disabled={trial || !groupSupported}
                        onChange={(event) => setForm(withGroup(form, event.target.checked))}
                        inputProps={{ 'data-testid': 'bulk-use-group' }}
                    />
                )}
                label={trial
                    ? 'בקשה אחת לקבוצה של מילים (בניסוי זה תמיד כך)'
                    : 'בקשה אחת לקבוצה של מילים: ההקלטה נחתכת לקבצים של המילים (מהיר וזול בהרבה)'}
            />
            {!groupSupported && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="bulk-group-unavailable">
                    ההקראה בקבוצות תהיה זמינה אחרי שהשרת יתעדכן. עד אז כל קובץ מוקרא בבקשה משלו.
                </Typography>
            )}
            {grouping && (
                <Box sx={{ mt: 1, mb: 1 }}>
                    <TextField
                        size="small"
                        label="מילים בקבוצה"
                        value={form.groupSize}
                        onChange={(event) => update({ groupSize: event.target.value })}
                        placeholder={String(groupLimits.defaultSize)}
                        helperText={`מ-${formatNumber(groupLimits.minSize)} עד ${formatNumber(groupLimits.maxSize)}. ריק: ${formatNumber(groupLimits.defaultSize)}. כדאי להתחיל בכמה עשרות ולהגדיל.`}
                        inputProps={{ dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bulk-group-size' }}
                        sx={{ minWidth: 220 }}
                    />
                    <Alert severity="info" sx={{ mt: 1 }} data-testid="bulk-group-note">
                        כל קבוצה נשלחת ל-Google בבקשה אחת, וההקלטה נחתכת לקבצים לפי השקט בין המילים. זה עובד רק עם Google, בלי ספק גיבוי ובקשה אחת בכל פעם:
                        כשהמכסה היומית נגמרת ההרצה מושהית, ואפשר להמשיך אותה. טקסטים ארוכים מ-{formatNumber(groupLimits.maxItemChars)} תווים לא נכללים בקבוצות (מכינים אותם בהרצה בלי קבוצה).
                        ההקלטה המלאה של כל קבוצה נשמרת בצד (פרטית, לא באתר), ואפשר להאזין לה במסך ההרצה, מילה אחרי מילה.
                    </Alert>
                </Box>
            )}

            <SectionTitle>1. אילו ערכים</SectionTitle>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <TextField
                    select
                    size="small"
                    label="מילון"
                    value={form.dictionary}
                    onChange={(event) => setForm(withDictionary(form, event.target.value, options))}
                    sx={{ minWidth: 220 }}
                    SelectProps={{ SelectDisplayProps: { 'data-testid': 'bulk-dictionary' } }}
                >
                    {dictionaries.map((dictionary) => (
                        <MenuItem key={dictionary.key} value={dictionary.key}>{DICTIONARY_INFO[dictionary.key]?.label ?? dictionary.label ?? dictionary.key}</MenuItem>
                    ))}
                </TextField>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={form.source}
                    onChange={(event, value) => value && update({ source: value })}
                    aria-label="איך לבחור את הערכים"
                >
                    <ToggleButton value={SOURCE_IDS} data-testid="bulk-source-ids">מזהי ערכים</ToggleButton>
                    <ToggleButton value={SOURCE_FILTER} data-testid="bulk-source-filter">סינון</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {form.source === SOURCE_IDS ? (
                <TextField
                    multiline
                    minRows={4}
                    maxRows={10}
                    fullWidth
                    label="מזהי הערכים"
                    value={form.idsText}
                    onChange={(event) => update({ idsText: event.target.value })}
                    error={parsedIds.invalid.length > 0}
                    helperText={idsHelper}
                    inputProps={{ dir: 'ltr', style: { fontFamily: 'monospace' }, 'data-testid': 'bulk-ids' }}
                />
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                    <TextField size="small" label="בנדים" placeholder="1, 2, 3" value={form.bandsText} onChange={(event) => update({ bandsText: event.target.value })}
                        helperText="מספרים מופרדים בפסיק" inputProps={{ dir: 'ltr', 'data-testid': 'bulk-bands' }} />
                    <Box>
                        <Typography variant="caption" color="text.secondary">רשימות</Typography>
                        <Box>
                            <ToggleButtonGroup size="small" value={form.lists} onChange={(event, value) => update({ lists: value })} aria-label="רשימות">
                                {LISTS.map((list) => <ToggleButton key={list} value={list} data-testid={`bulk-list-${list}`}>{list}</ToggleButton>)}
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                    <Box />
                    <TextField size="small" label="עדיפות מ-" value={form.priorityMin} onChange={(event) => update({ priorityMin: event.target.value })} inputProps={{ dir: 'ltr', inputMode: 'decimal' }} />
                    <TextField size="small" label="עדיפות עד" value={form.priorityMax} onChange={(event) => update({ priorityMax: event.target.value })} inputProps={{ dir: 'ltr', inputMode: 'decimal' }} />
                    <Box />
                    <TextField size="small" type="date" label="נוצר מתאריך" value={form.createdFrom} onChange={(event) => update({ createdFrom: event.target.value })} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" type="date" label="נוצר עד תאריך" value={form.createdTo} onChange={(event) => update({ createdTo: event.target.value })} InputLabelProps={{ shrink: true }} />
                    <Box />
                    <TextField size="small" label="כמה ערכים לבדוק" placeholder={String(limits.defaultEntryLimit)} value={form.limit} onChange={(event) => update({ limit: event.target.value })}
                        helperText={`ברירת מחדל ${formatNumber(limits.defaultEntryLimit)}, עד ${formatNumber(limits.maxEntries)}`} inputProps={{ dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bulk-limit' }} />
                    <TextField size="small" label="המשך אחרי מזהה" value={form.afterId} onChange={(event) => update({ afterId: event.target.value })}
                        helperText="להמשך הרצה גדולה: המזהה האחרון שנבדק" inputProps={{ dir: 'ltr', 'data-testid': 'bulk-after-id' }} />
                </Box>
            )}

            <SectionTitle>2. מה מוקרא</SectionTitle>
            <FormGroup row>
                {fieldNames.map((field) => (
                    <FormControlLabel
                        key={field}
                        control={(
                            <Checkbox
                                checked={form.fields.includes(field)}
                                onChange={() => setForm(withFields(form, toggle(form.fields, field).sort((a, b) => fieldNames.indexOf(a) - fieldNames.indexOf(b)), options))}
                                inputProps={{ 'data-testid': `bulk-field-${field}` }}
                            />
                        )}
                        label={`${FIELD_INFO[field] ?? field} (${(fieldMap[field] ?? []).map(languageLabel).join(', ')})`}
                    />
                ))}
            </FormGroup>
            {impliedLanguages(options, form.dictionary, form.fields).length > 1 && (
                <FormGroup row>
                    {impliedLanguages(options, form.dictionary, form.fields).map((language) => (
                        <FormControlLabel
                            key={language}
                            control={<Checkbox checked={form.languages.includes(language)} onChange={() => update({ languages: toggle(form.languages, language) })} inputProps={{ 'data-testid': `bulk-language-${language}` }} />}
                            label={languageLabel(language)}
                        />
                    ))}
                </FormGroup>
            )}
            <FormGroup row>
                {GENDERS.map((gender) => (
                    <FormControlLabel
                        key={gender}
                        control={<Checkbox checked={form.genders.includes(gender)} onChange={() => update({ genders: toggle(form.genders, gender) })} inputProps={{ 'data-testid': `bulk-gender-${gender}` }} />}
                        label={`קול ${GENDER_LABELS[gender]}`}
                    />
                ))}
            </FormGroup>

            <SectionTitle>3. מי מקריא</SectionTitle>
            <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={form.provider}
                    onChange={(event, value) => value && update({ provider: value })}
                    aria-label="ספק"
                >
                    <ToggleButton value="google" data-testid="bulk-provider-google">{PROVIDER_LABELS.google}</ToggleButton>
                    <ToggleButton value="inworld" disabled={grouping} data-testid="bulk-provider-inworld">{PROVIDER_LABELS.inworld}</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <VoicesGrid
                form={form}
                setForm={setForm}
                options={options}
                provider={form.provider}
                languages={languages}
                genders={genders}
                title={`קולות ${PROVIDER_LABELS[form.provider]}`}
                note="הקולות נבחרים להרצה עצמה. הם לא נלקחים מההגדרות של היצירה האוטומטית."
            />
            <FormControlLabel
                control={<Switch checked={form.useFallback} disabled={grouping} onChange={() => update({ useFallback: !form.useFallback })} inputProps={{ 'data-testid': 'bulk-use-fallback' }} />}
                label={grouping
                    ? 'ספק גיבוי: לא בהקראה בקבוצות'
                    : `ספק גיבוי: ${PROVIDER_LABELS[fallbackProvider]} (נכנס כשהספק הראשי נעצר בגלל מכסה יומית, מפתח לא תקין או שנגמר האשראי)`}
            />
            {form.useFallback && !grouping && (
                <Box sx={{ mt: 1 }}>
                    <VoicesGrid
                        form={form}
                        setForm={setForm}
                        options={options}
                        provider={fallbackProvider}
                        languages={languages}
                        genders={genders}
                        title={`קולות הגיבוי (${PROVIDER_LABELS[fallbackProvider]})`}
                    />
                </Box>
            )}

            <Accordion variant="outlined" disableGutters sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>הגבלות (לא חובה)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                        <TextField size="small" label="תקרת עלות (דולר)" value={form.maxCostUsd} onChange={(event) => update({ maxCostUsd: event.target.value })}
                            placeholder="אוטומטית" helperText={`ריק: פי שניים מההערכה (לפחות 1, עד ${limits.maxCostUsd})`} inputProps={{ dir: 'ltr', inputMode: 'decimal', 'data-testid': 'bulk-max-cost' }} />
                        <TextField size="small" label="בקשות בדקה" value={form.rpm} onChange={(event) => update({ rpm: event.target.value })}
                            placeholder={String(pacingOf(options, form.provider).rpm)} helperText={`ברירת מחדל לספק, עד ${limits.maxRpm}`} inputProps={{ dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bulk-rpm' }} />
                        <TextField size="small" label="בקשות במקביל" value={grouping ? '' : form.concurrency} onChange={(event) => update({ concurrency: event.target.value })} disabled={grouping}
                            placeholder={grouping ? '1' : String(pacingOf(options, form.provider).concurrency)}
                            helperText={grouping ? 'בקבוצות: תמיד בקשה אחת בכל פעם' : `ברירת מחדל לספק, עד ${limits.maxConcurrency}`}
                            inputProps={{ dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bulk-concurrency' }} />
                    </Box>
                </AccordionDetails>
            </Accordion>

            {showErrors && errors.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }} data-testid="bulk-form-errors">
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {errors.map((error) => <li key={error}>{error}</li>)}
                    </Box>
                </Alert>
            )}

            <Box sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={onPlan}
                    disabled={planning}
                    startIcon={planning ? <CircularProgress size={16} color="inherit" /> : null}
                    data-testid="bulk-plan"
                >
                    {replacing ? 'בדיקת תוכנית (לא משנה כלום)' : 'בדיקת תוכנית (לא יוצר כלום)'}
                </Button>
            </Box>
        </Paper>
    );
};

export default BulkRunForm;
